// src/components/missions/MissionCard.jsx
import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Clock,
  Package,
  Phone,
  Navigation,
  ChevronRight,
  Zap,
  User
} from 'lucide-react';

const MissionCard = ({
  mission,
  variant = 'available', // 'available' | 'current' | 'completed'
  onAccept,
  onView,
  onComplete,
  onNavigate,
  onCall,
  index = 0
}) => {
  const isUrgent = mission.is_urgent || mission.method === 'express';
  const isCurrent = variant === 'current';
  const isCompleted = variant === 'completed';

  const getStatusColor = () => {
    switch (mission.status) {
      case 'in_transit':
      case 'out_for_delivery':
        return 'bg-blue-500';
      case 'delivered':
        return 'bg-emerald-500';
      case 'failed_delivery':
        return 'bg-red-500';
      default:
        return 'bg-orange-500';
    }
  };

  const getStatusText = () => {
    switch (mission.status) {
      case 'pending':
        return 'En attente';
      case 'processing':
        return 'En préparation';
      case 'shipped':
        return 'Expédié';
      case 'in_transit':
        return 'En transit';
      case 'out_for_delivery':
        return 'En livraison';
      case 'delivered':
        return 'Livré';
      case 'failed_delivery':
        return 'Échec';
      default:
        return mission.status;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -2 }}
      className="relative group"
    >
      {/* Glass Card */}
      <div
        className={`relative overflow-hidden rounded-2xl border transition-all ${
          isCurrent
            ? 'bg-gradient-to-br from-emerald-50 to-white border-emerald-200 shadow-lg shadow-emerald-100'
            : isCompleted
            ? 'bg-gray-50 border-gray-200'
            : 'bg-white/80 backdrop-blur-sm border-gray-200/50 hover:border-emerald-300 hover:shadow-lg'
        }`}
      >
        {/* Urgent Badge */}
        {isUrgent && !isCompleted && (
          <div className="absolute top-0 right-0">
            <div className="bg-gradient-to-br from-red-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-xl flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Express
            </div>
          </div>
        )}

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div
                className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  isCurrent
                    ? 'bg-emerald-100 text-emerald-600'
                    : isCompleted
                    ? 'bg-gray-100 text-gray-500'
                    : 'bg-orange-100 text-orange-600'
                }`}
              >
                <Package className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">
                  {mission.order_number || `#${mission.tracking_number?.slice(-8) || mission.id?.slice(-8)}`}
                </p>
                {mission.order_number && mission.tracking_number && (
                  <p className="text-xs text-gray-400">{mission.tracking_number}</p>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor()}`}
                  >
                    {getStatusText()}
                  </span>
                  {mission.method && (
                    <span className="text-xs text-gray-500 capitalize">
                      {mission.method}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="text-right">
              <p className="text-lg font-bold text-emerald-600">
                {(mission.cost || mission.driver_fee || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">FCFA</p>
            </div>
          </div>

          {/* Addresses */}
          <div className="space-y-2 mb-4">
            {/* From Address */}
            {mission.from_address && (
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Récupération</p>
                  <p className="text-sm text-gray-700 truncate">{mission.from_address}</p>
                </div>
              </div>
            )}

            {/* Dotted line connector */}
            {mission.from_address && mission.to_address && (
              <div className="flex items-center gap-2 pl-3">
                <div className="w-0.5 h-4 border-l-2 border-dashed border-gray-300" />
              </div>
            )}

            {/* To Address */}
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin className="w-3 h-3 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Livraison</p>
                <p className="text-sm text-gray-700 truncate">
                  {mission.to_address || 'Adresse non spécifiée'}
                </p>
              </div>
            </div>
          </div>

          {/* Client Info (for current missions) */}
          {isCurrent && mission.recipient_name && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl mb-4">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-5 h-5 text-gray-500" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{mission.recipient_name}</p>
                {mission.recipient_phone && (
                  <p className="text-sm text-gray-500">{mission.recipient_phone}</p>
                )}
              </div>
              {mission.recipient_phone && (
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => onCall && onCall(mission.recipient_phone)}
                  className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600"
                >
                  <Phone className="w-5 h-5" />
                </motion.button>
              )}
            </div>
          )}

          {/* Meta Info */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            {mission.estimated_delivery_time && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{mission.estimated_delivery_time} min</span>
              </div>
            )}
            {mission.distance && (
              <div className="flex items-center gap-1">
                <Navigation className="w-4 h-4" />
                <span>{mission.distance} km</span>
              </div>
            )}
            {mission.delivery_zone && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{mission.delivery_zone}</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            {variant === 'available' && (
              <>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onAccept && onAccept(mission)}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all shadow-lg shadow-emerald-200"
                >
                  Accepter la mission
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onView && onView(mission)}
                  className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </motion.button>
              </>
            )}

            {variant === 'current' && (
              <>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onNavigate && onNavigate(mission)}
                  className="flex-1 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all flex items-center justify-center gap-2"
                >
                  <Navigation className="w-5 h-5" />
                  Naviguer
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onComplete && onComplete(mission)}
                  className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all"
                >
                  Livré
                </motion.button>
              </>
            )}

            {variant === 'completed' && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onView && onView(mission)}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
              >
                Voir les détails
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            )}
          </div>
        </div>

        {/* Delivery Instructions */}
        {isCurrent && mission.delivery_instructions && (
          <div className="px-4 pb-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs font-medium text-amber-800 mb-1">Instructions de livraison</p>
              <p className="text-sm text-amber-700">{mission.delivery_instructions}</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default MissionCard;
